/**
 * PDP urgency helpers + Buy More volume tiers + frequently paired selection.
 * Tier `discount` = PDP price preview %. Tier `code` = discount code applied on ATC.
 */
(() => {
  function money(cents) {
    if (cents == null || Number.isNaN(Number(cents))) return '';
    const value = Number(cents);
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(value, window.theme?.moneyFormat || undefined);
      } catch (e) {}
    }
    return 'Rs.' + (value / 100).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function selectedBundles() {
    return Array.from(document.querySelectorAll('.pdp-bundle__check:checked'))
      .map((el) => {
        const id = Number(el.getAttribute('data-bundle-variant'));
        if (!id) return null;
        return {
          id,
          quantity: 1,
        };
      })
      .filter(Boolean);
  }

  function selectedTierDiscountCode() {
    const checked = document.querySelector('input[name="pdp_volume_tier"]:checked');
    return (checked?.getAttribute('data-discount-code') || '').trim();
  }

  async function applyDiscountCode(code) {
    const root = shopRoot();
    const normalized = (code || '').trim();

    try {
      if (!normalized) {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ discount: '' }),
        });
        return;
      }

      // Sets discount cookie for cart/checkout
      await fetch(`${root}discount/${encodeURIComponent(normalized)}`, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      // Also bind to cart JSON so drawer refresh picks it up
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: normalized }),
      });
    } catch (e) {
      // Non-blocking — ATC should still succeed
    }
  }

  function calcTierTotals(unitPrice, unitCompare, qty, discountPercent) {
    const lineBase = unitPrice * qty;
    const lineCompare = Math.max(unitCompare, unitPrice) * qty;
    const disc = Math.max(0, Math.min(100, Number(discountPercent) || 0));
    const linePrice =
      disc > 0 ? Math.round((lineBase * (100 - disc)) / 100) : lineBase;
    const lineSave = Math.max(0, lineCompare - linePrice);
    return { lineBase, lineCompare, linePrice, lineSave, disc };
  }

  function updateTierCard(label, totals) {
    if (!label) return;
    const priceEl = label.querySelector('.pdp-volume__price');
    const compareEl = label.querySelector('.pdp-volume__compare');
    const saveEl = label.querySelector('.pdp-volume__save');

    if (priceEl) priceEl.textContent = money(totals.linePrice);

    if (compareEl) {
      if (totals.lineCompare > totals.linePrice) {
        compareEl.hidden = false;
        compareEl.textContent = money(totals.lineCompare);
      } else {
        compareEl.hidden = true;
      }
    }

    if (saveEl) {
      if (totals.lineSave > 0) {
        saveEl.hidden = false;
        saveEl.textContent = 'You Save ' + money(totals.lineSave);
      } else {
        saveEl.hidden = true;
      }
    }
  }

  let syncingTier = false;

  function syncTierUI(input) {
    if (!input || syncingTier) return;
    syncingTier = true;
    try {
      const volume = document.querySelector('[data-pdp-volume]');
      const qty = Number(input.getAttribute('data-qty') || input.value || 1) || 1;
      const linePrice = Number(input.getAttribute('data-line-price') || 0);
      const lineCompare = Number(input.getAttribute('data-line-compare') || 0);
      const lineSave = Number(input.getAttribute('data-line-save') || 0);
      const volumePct = Number(input.getAttribute('data-discount') || 0);
      const basePct = Number(volume?.getAttribute('data-base-sale-percent') || 0);

      const qtyInput = document.getElementById('pdpQtyInput');
      if (qtyInput && String(qtyInput.value) !== String(qty)) {
        qtyInput.value = String(qty);
      }

      const priceCurrent = document.getElementById('pdpPriceCurrent');
      const priceCompare = document.getElementById('pdpPriceCompare');
      const priceSave = document.getElementById('pdpPriceSave');
      const stickyPrice = document.getElementById('pdpStickyPrice');

      if (priceCurrent && linePrice > 0) {
        priceCurrent.textContent = money(linePrice);
      }

      if (priceCompare) {
        if (lineCompare > linePrice) {
          priceCompare.hidden = false;
          priceCompare.textContent = money(lineCompare);
        } else {
          priceCompare.hidden = true;
          priceCompare.textContent = '';
        }
      }

      if (priceSave) {
        const actualPct =
          lineCompare > 0 ? Math.round((lineSave / lineCompare) * 100) : 0;
        const stackedPct = Math.max(0, basePct) + Math.max(0, volumePct);
        const displayPct = volumePct > 0 ? stackedPct : actualPct || basePct;

        if (lineSave > 0 && lineCompare > linePrice) {
          priceSave.hidden = false;
          priceSave.textContent = 'Save ' + money(lineSave) + ' (' + displayPct + '%)';
        } else {
          priceSave.hidden = true;
          priceSave.textContent = '';
        }
      }

      if (stickyPrice && linePrice > 0) {
        stickyPrice.textContent = money(linePrice);
      }

      document.querySelectorAll('.pdp-volume__tier').forEach((tier) => {
        const checked = tier.querySelector('input')?.checked;
        tier.classList.toggle('is-selected', Boolean(checked));
      });
    } finally {
      syncingTier = false;
    }
  }

  function refreshForVariant(unitPrice, unitCompare) {
    const volume = document.querySelector('[data-pdp-volume]');
    if (!volume) return;

    const price = Number(unitPrice) || 0;
    let compare = Number(unitCompare) || 0;
    if (!compare || compare < price) compare = price;

    volume.setAttribute('data-unit-price', String(price));
    volume.setAttribute('data-unit-compare', String(compare));

    const basePct =
      compare > price && compare > 0
        ? Math.round(((compare - price) * 100) / compare)
        : 0;
    volume.setAttribute('data-base-sale-percent', String(basePct));

    volume.querySelectorAll('input[name="pdp_volume_tier"]').forEach((input) => {
      const qty = Number(input.getAttribute('data-qty') || 1) || 1;
      const disc = Number(input.getAttribute('data-discount') || 0) || 0;
      const totals = calcTierTotals(price, compare, qty, disc);
      input.setAttribute('data-line-price', String(totals.linePrice));
      input.setAttribute('data-line-compare', String(totals.lineCompare));
      input.setAttribute('data-line-save', String(totals.lineSave));
      updateTierCard(input.closest('.pdp-volume__tier'), totals);
    });

    const checked =
      volume.querySelector('input[name="pdp_volume_tier"]:checked') ||
      volume.querySelector('input[name="pdp_volume_tier"]');
    if (checked) syncTierUI(checked);
  }

  function initViewingNow() {
    const el = document.querySelector('[data-pdp-viewing-now]');
    if (!el) return;

    const countEl = el.querySelector('[data-viewing-count]');
    const labelEl = el.querySelector('[data-viewing-label]');
    if (!countEl) return;

    let min = Number(el.getAttribute('data-min') || 6);
    let max = Number(el.getAttribute('data-max') || 18);
    if (!Number.isFinite(min) || min < 1) min = 1;
    if (!Number.isFinite(max) || max < min) max = min;

    let current = min + Math.floor(Math.random() * (max - min + 1));

    function render() {
      countEl.textContent = String(current);
      if (labelEl) {
        labelEl.textContent =
          current === 1 ? 'person viewing this right now' : 'people viewing this right now';
      }
    }

    render();

    window.setInterval(() => {
      const delta = Math.random() < 0.5 ? -1 : 1;
      let next = current + delta;
      if (next < min) next = min;
      if (next > max) next = max;
      if (Math.random() < 0.2) {
        next = current + (Math.random() < 0.5 ? -2 : 2);
        if (next < min) next = min;
        if (next > max) next = max;
      }
      current = next;
      render();
    }, 4000 + Math.floor(Math.random() * 3000));
  }

  function initVolume() {
    const volume = document.querySelector('[data-pdp-volume]');
    if (!volume) return;

    volume.querySelectorAll('input[name="pdp_volume_tier"]').forEach((input) => {
      input.addEventListener('change', () => {
        syncTierUI(input);
        // Prefetch cookie so ATC / Buy now can pick it up
        applyDiscountCode(input.getAttribute('data-discount-code') || '');
      });
    });

    const checked = volume.querySelector('input[name="pdp_volume_tier"]:checked');
    if (checked) syncTierUI(checked);

    const qtyInput = document.getElementById('pdpQtyInput');
    if (qtyInput) {
      const syncFromQty = () => {
        if (syncingTier) return;
        const qty = Number(qtyInput.value || 1) || 1;
        const match = volume.querySelector(
          `input[name="pdp_volume_tier"][data-qty="${qty}"]`
        );
        if (match) {
          if (!match.checked) match.checked = true;
          syncTierUI(match);
          applyDiscountCode(match.getAttribute('data-discount-code') || '');
        }
      };
      qtyInput.addEventListener('change', syncFromQty);
      qtyInput.addEventListener('input', syncFromQty);
    }

    window.setTimeout(() => {
      const checkedAgain = volume.querySelector('input[name="pdp_volume_tier"]:checked');
      if (checkedAgain) syncTierUI(checkedAgain);
    }, 0);
  }

  function init() {
    initViewingNow();
    initVolume();
    window.NimizoPdpOffers = {
      selectedBundles,
      selectedTierDiscountCode,
      applyDiscountCode,
      syncTierUI,
      refreshForVariant,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
